import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import {
  Spinner,
  Alert,
  Button,
  Table,
  Row,
  Col,
  Input,
  Label,
  Badge,
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { getLogs, getLogStats } from "../ApiCalls/systemLogs";
import PageHeader from "./components/PageHeader";
import "./styles/systemLogs.scss";

const select = (state) => {
  return {
    userId: state.auth.userId,
    token: state.auth.token,
  };
};

const LOG_TYPES = [
  { value: "LOG", label: "Log", color: "info" },
  { value: "ERROR", label: "Error", color: "danger" },
  { value: "EXCEPTION", label: "Exception", color: "danger" },
  { value: "INFO", label: "Info", color: "primary" },
  { value: "WARNING", label: "Warning", color: "warning" },
  { value: "USER_DELETION", label: "User Deletion", color: "danger" },
  { value: "VIRUS_FOUND_IN_DOCUMENT", label: "Virus Found", color: "danger" },
];

const formatNumber = (value) => Number(value || 0).toLocaleString("en-US");

// Logs written before `type` was enforced come back grouped under an empty _id.
const UNTYPED_LABEL = "Sin tipo";

const getLogTypeLabel = (type) => {
  if (!type) return UNTYPED_LABEL;
  return LOG_TYPES.find((t) => t.value === type)?.label || type;
};

const getLogTypeBadgeColor = (type) => {
  if (!type) return "secondary";
  return LOG_TYPES.find((t) => t.value === type)?.color || "secondary";
};

const formatContent = (content) => {
  if (content === null || content === undefined) return "—";
  if (typeof content === "object") {
    return JSON.stringify(content, null, 2);
  }
  return String(content);
};

const emptyFilters = {
  type: "",
  dateFrom: "",
  dateTo: "",
  limit: 50,
  offset: 0,
};

function SystemLogs(props) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);

  const [filters, setFilters] = useState(emptyFilters);

  // The API expects an inclusive upper bound, so push dateTo to end of day.
  const buildQuery = useCallback((currentFilters) => {
    const query = {
      limit: currentFilters.limit,
      offset: currentFilters.offset,
    };
    if (currentFilters.type) query.type = currentFilters.type;
    if (currentFilters.dateFrom) query.dateFrom = currentFilters.dateFrom;
    if (currentFilters.dateTo) {
      query.dateTo = `${currentFilters.dateTo}T23:59:59.999`;
    }
    return query;
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getLogs(props.token, buildQuery(filters));
      setLogs(data.logs || []);
      setPagination(data.pagination || { total: 0 });
    } catch (err) {
      setError(err.message || "Failed to load logs");
    } finally {
      setLoading(false);
    }
  }, [props.token, filters, buildQuery]);

  const fetchStats = useCallback(async () => {
    try {
      const query = {};
      if (filters.dateFrom) query.dateFrom = filters.dateFrom;
      if (filters.dateTo) query.dateTo = `${filters.dateTo}T23:59:59.999`;
      setStats(await getLogStats(props.token, query));
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  }, [props.token, filters.dateFrom, filters.dateTo]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: key === "limit" ? Number(value) : value,
      offset: 0,
    }));
  };

  const goToPage = (direction) => {
    setFilters((prev) => ({
      ...prev,
      offset: Math.max(0, prev.offset + direction * Number(prev.limit)),
    }));
  };

  const hasActiveFilters =
    filters.type || filters.dateFrom || filters.dateTo;

  const pageStart = logs.length === 0 ? 0 : filters.offset + 1;
  const pageEnd = filters.offset + logs.length;
  const hasNextPage = pageEnd < (pagination.total || 0);

  if (loading && logs.length === 0) {
    return (
      <div className="system-logs-page">
        <div className="loading-container">
          <Spinner color="primary" />
          <p>Loading system logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="system-logs-page">
      <PageHeader
        title="Logs del sistema"
        subtitle="Auditoría de eventos, errores y excepciones de la plataforma"
      />

      {/* Page Content */}
      <div className="page-content">
        {error && (
          <Row>
            <Col xs={12}>
              <Alert color="danger">{error}</Alert>
            </Col>
          </Row>
        )}

        {/* Stats Cards */}
        <Row className="mb-4">
          <Col xs={12}>
            <div className="stats-cards">
              <Card className="stat-card">
                <CardBody>
                  <div className="stat-value">{formatNumber(stats.total)}</div>
                  <div className="stat-label">Total Logs</div>
                </CardBody>
              </Card>

              {stats.byType &&
                stats.byType.map((typeStats) => (
                  <Card key={typeStats._id || "untyped"} className="stat-card">
                    <CardBody>
                      <div className="stat-value">
                        {formatNumber(typeStats.count)}
                      </div>
                      <div className="stat-label">
                        {getLogTypeLabel(typeStats._id)}
                      </div>
                      <Badge
                        color={getLogTypeBadgeColor(typeStats._id)}
                        className="stat-badge"
                      >
                        {typeStats._id || "UNTYPED"}
                      </Badge>
                    </CardBody>
                  </Card>
                ))}
            </div>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="filters-card mb-4">
          <CardBody>
            <Row className="align-items-end g-3">
              <Col md={3}>
                <Label for="filter-type" className="filter-label">
                  Type
                </Label>
                <Input
                  id="filter-type"
                  type="select"
                  value={filters.type}
                  onChange={(e) => handleFilterChange("type", e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Types</option>
                  {LOG_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={2}>
                <Label for="filter-date-from" className="filter-label">
                  From
                </Label>
                <Input
                  id="filter-date-from"
                  type="date"
                  value={filters.dateFrom}
                  max={filters.dateTo || undefined}
                  onChange={(e) =>
                    handleFilterChange("dateFrom", e.target.value)
                  }
                  className="filter-select"
                />
              </Col>
              <Col md={2}>
                <Label for="filter-date-to" className="filter-label">
                  To
                </Label>
                <Input
                  id="filter-date-to"
                  type="date"
                  value={filters.dateTo}
                  min={filters.dateFrom || undefined}
                  onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                  className="filter-select"
                />
              </Col>
              <Col md={2}>
                <Label for="filter-limit" className="filter-label">
                  Per page
                </Label>
                <Input
                  id="filter-limit"
                  type="select"
                  value={filters.limit}
                  onChange={(e) => handleFilterChange("limit", e.target.value)}
                  className="filter-select"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </Input>
              </Col>
              <Col md={3} className="filter-actions">
                <Button
                  color="primary"
                  outline
                  onClick={fetchLogs}
                  disabled={loading}
                >
                  <i className="fas fa-sync" /> Refresh
                </Button>
                {hasActiveFilters && (
                  <Button
                    color="secondary"
                    outline
                    onClick={() => setFilters(emptyFilters)}
                    disabled={loading}
                  >
                    <i className="fas fa-times" /> Clear
                  </Button>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Logs Table */}
        {logs.length === 0 ? (
          <Alert color="info">No logs found matching the selected filters.</Alert>
        ) : (
          <div className="logs-table-container">
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Context</th>
                  <th>User ID</th>
                  <th>Business ID</th>
                  <th>Content Preview</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr
                    key={log._id}
                    className="log-row"
                    onClick={() => setSelectedLog(log)}
                    title="Click to see the full log"
                  >
                    <td>
                      <Badge color={getLogTypeBadgeColor(log.type)}>
                        {getLogTypeLabel(log.type)}
                      </Badge>
                    </td>
                    <td className="date-cell">
                      {log.dateGenerated
                        ? new Date(log.dateGenerated).toLocaleString()
                        : "—"}
                    </td>
                    <td className="context-cell text-muted">
                      {log.context || "—"}
                    </td>
                    <td className="user-id-cell text-muted">
                      {log.userId ? `${log.userId.substring(0, 8)}...` : "—"}
                    </td>
                    <td className="business-id-cell text-muted">
                      {log.mantisBusinessId
                        ? `${log.mantisBusinessId.substring(0, 8)}...`
                        : "—"}
                    </td>
                    <td className="content-preview">
                      {formatContent(log.content).substring(0, 50)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <Row className="mt-3 align-items-center">
            <Col md={6}>
              <span className="text-muted">
                Showing {formatNumber(pageStart)}–{formatNumber(pageEnd)} of{" "}
                {formatNumber(pagination.total)}
              </span>
            </Col>
            <Col md={6} className="text-md-end">
              <Button
                color="secondary"
                outline
                size="sm"
                className="me-2"
                onClick={() => goToPage(-1)}
                disabled={loading || filters.offset === 0}
              >
                <i className="fas fa-chevron-left" /> Previous
              </Button>
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={() => goToPage(1)}
                disabled={loading || !hasNextPage}
              >
                Next <i className="fas fa-chevron-right" />
              </Button>
            </Col>
          </Row>
        )}
      </div>

      {/* Log detail */}
      <Modal
        isOpen={!!selectedLog}
        toggle={() => setSelectedLog(null)}
        size="lg"
        className="log-detail-modal"
      >
        <ModalHeader toggle={() => setSelectedLog(null)}>
          <Badge color={getLogTypeBadgeColor(selectedLog?.type)}>
            {getLogTypeLabel(selectedLog?.type)}
          </Badge>
          <span className="detail-header-context">
            {selectedLog?.context || "Log"}
          </span>
        </ModalHeader>
        <ModalBody>
          {selectedLog && (
            <>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Date</span>
                  <span className="detail-value">
                    {selectedLog.dateGenerated
                      ? new Date(selectedLog.dateGenerated).toLocaleString()
                      : "—"}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Log ID</span>
                  <span className="detail-value monospace">
                    {selectedLog._id}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">User ID</span>
                  <span className="detail-value monospace">
                    {selectedLog.userId || "—"}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Business ID</span>
                  <span className="detail-value monospace">
                    {selectedLog.mantisBusinessId || "—"}
                  </span>
                </div>
              </div>

              <div className="detail-content">
                <span className="detail-label">Content</span>
                <pre>{formatContent(selectedLog.content)}</pre>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={() => {
              navigator.clipboard.writeText(
                JSON.stringify(selectedLog, null, 2)
              );
            }}
          >
            <i className="fas fa-copy" /> Copy JSON
          </Button>
          <Button color="primary" onClick={() => setSelectedLog(null)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default connect(select)(SystemLogs);
